import { z } from 'zod';

/** `cloud` is the hosted deployment; `local` is `crowdex serve` on the user's machine. */
export const serverProfileSchema = z.enum(['cloud', 'local']);
export type ServerProfile = z.infer<typeof serverProfileSchema>;

export const appBootstrapSchema = z.object({
  profile: serverProfileSchema.default('cloud'),
  user: z.object({ id: z.string(), email: z.email(), name: z.string() }),
  permissions: z.array(z.string()),
  /** The user's organisation and role there (Zitadel's, in the cloud); absent when it has none. */
  organization: z
    .object({ name: z.string(), role: z.enum(['admin', 'member']) })
    .nullable()
    .optional(),
  /**
   * The user's Crowlingo sign-in predates a service the cloud now calls for them (its audience is
   * missing from their tokens): the app signs in again silently (Zitadel's `prompt=none`).
   */
  signInAgain: z.boolean().optional(),
});

export type AppBootstrap = z.infer<typeof appBootstrapSchema>;

export const apiIssueSchema = z.object({ path: z.string(), message: z.string() });
export const apiErrorSchema = z.object({
  code: z.enum(['unauthorized', 'invalid_request', 'not_found', 'conflict', 'internal_error']),
  message: z.string(),
  issues: z.array(apiIssueSchema),
});

/**
 * `{ ok: true, data }`, or a refusal `{ error: { code, message, issues } }` (CONVENTIONS.md,
 * « Errors »); a former refusal's `ok: false` is still read.
 */
export function apiResultSchema<TSchema extends z.ZodType>(data: TSchema) {
  return z.union([
    z.object({ ok: z.literal(true), data }),
    z.object({ ok: z.literal(false).optional(), error: apiErrorSchema }),
  ]);
}
export * from './account';
export * from './agent-turns';
export * from './apps';
export * from './assistant';
export * from './assistants';
export * from './cloud-link';
export * from './code-forges';
export * from './computer-use';
export * from './conversation-changes';
export * from './conversation-copy';
export * from './conversation-handoff';
export * from './conversation-panel';
export * from './desktop-updates';
export * from './local-conversations';
export * from './model-consumers';
export * from './persona-reply';
export * from './started-work';
